import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { pdf } from '@react-pdf/renderer';
import AlertBox from '../../Components/AlertBox';
import SalarySlipPDF from './SalarySlipPDF';

// Enhanced working time calculation with proper formatting (Same as AttendanceReports.js)
const calculateEnhancedWorkingTime = (inTime, outTime) => {
  if (!inTime || !outTime) return '--:--';

  try {
    const inDate = new Date(inTime);
    const outDate = new Date(outTime);

    if (isNaN(inDate.getTime()) || isNaN(outDate.getTime())) return '--:--';

    const diffMs = outDate - inDate;
    if (diffMs <= 0) return '--:--';

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // Format: "8:30", "7:45", etc.
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  } catch (error) {
    return '--:--';
  }
};

// Calculate working minutes for attendance records (Same as AttendanceReports.js)
const calculateWorkingMinutes = (inTime, outTime) => {
  if (!inTime || !outTime) return 0;

  try {
    const inDate = new Date(inTime);
    const outDate = new Date(outTime);

    if (isNaN(inDate.getTime()) || isNaN(outDate.getTime())) return 0;

    const diffMs = outDate - inDate;
    if (diffMs <= 0) return 0;

    return Math.floor(diffMs / (1000 * 60)); // Return minutes
  } catch (error) {
    return 0;
  }
};

// Calculate attendance status based on shift hours vs actual working hours (Same as AttendanceReports.js)
const calculateShiftBasedStatus = (record, employeeShiftInfo) => {
  // If no punch times, return absent
  if (!record.inTime) {
    return {
      status: 'Absent',
      reason: 'No punch in time recorded',
      workingMinutes: 0,
      shiftMinutes: 0,
      completionPercentage: 0
    };
  }

  // If no punch out, return incomplete
  if (!record.outTime) {
    // If we have punch in but no punch out, mark as Present (matching Android/AttendanceReports logic)
    return {
      status: 'Present',
      reason: 'Punched in (No punch out recorded)',
      workingMinutes: 0,
      shiftMinutes: 480, // Default
      completionPercentage: 0
    };
  }

  // Calculate actual working minutes
  const workingMinutes = calculateWorkingMinutes(record.inTime, record.outTime);

  // Get shift duration (default to 8 hours = 480 minutes if no shift info)
  let shiftMinutes = 480; // Default 8 hours

  if (employeeShiftInfo && employeeShiftInfo.shiftStartTime && employeeShiftInfo.shiftEndTime) {
    // Calculate shift duration from shift times
    const shiftStart = new Date(`2000-01-01T${employeeShiftInfo.shiftStartTime}`);
    const shiftEnd = new Date(`2000-01-01T${employeeShiftInfo.shiftEndTime}`);

    if (!isNaN(shiftStart.getTime()) && !isNaN(shiftEnd.getTime())) {
      const shiftDiffMs = shiftEnd - shiftStart;
      if (shiftDiffMs > 0) {
        shiftMinutes = Math.floor(shiftDiffMs / (1000 * 60));
      }
    }
  } else if (employeeShiftInfo && employeeShiftInfo.workingHours) {
    // Use working hours if available
    shiftMinutes = employeeShiftInfo.workingHours * 60;
  }

  // Calculate completion percentage
  const completionPercentage = shiftMinutes > 0 ? Math.round((workingMinutes / shiftMinutes) * 100) : 0;

  // Determine status based on working hours vs shift hours
  let status = 'Present';
  let reason = 'Full shift completed';

  // Leniency: If completion is >= 90%, mark as Present.
  if (completionPercentage >= 90) {
    status = 'Present';
    reason = `Shift completed (${completionPercentage}%)`;
  } else if (workingMinutes > 0) {
    status = 'Half Day';
    reason = `Short shift (${completionPercentage}% completed)`;
  } else {
    status = 'Present';
    reason = 'Record exists but zero duration';
  }

  return {
    status,
    reason,
    workingMinutes,
    shiftMinutes,
    completionPercentage,
    workingHours: (workingMinutes / 60).toFixed(2),
    shiftHours: (shiftMinutes / 60).toFixed(2)
  };
};



// Helper to format date as YYYY-MM-DD in LOCAL time (Avoids timezone shifts)
const formatDateLocal = (date) => {
  if (!date || isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const ResponsiveSelect = ({ value, onChange, options, placeholder, name, required, disabled, disabledText }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOpt = options.find(o => String(o.value) === String(value));
  const displayLabel = selectedOpt ? selectedOpt.label : (disabledText || placeholder);

  return (
    <div className="w-full relative">
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className="desktop-select w-full px-4 py-3 bg-white/80 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
      >
        <option value="">{disabledText || placeholder}</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      <div className="mobile-custom-select" ref={wrapperRef}>
        <div
          className={`mcs-trigger w-full px-4 py-3 bg-white/80 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 flex justify-between items-center ${disabled ? 'opacity-70 bg-gray-100' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="mcs-label text-left truncate text-gray-700 font-medium">{displayLabel}</div>
          <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
        </div>
        {isOpen && !disabled && (
          <div className="mcs-dropdown absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-2xl max-h-[250px] overflow-y-auto left-0 right-0">
            <div
              className={`mcs-option p-4 border-b border-gray-100 ${!value ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
              onClick={() => {
                onChange({ target: { name, value: '' } });
                setIsOpen(false);
              }}
            >
              {placeholder}
            </div>
            {options.map(opt => (
              <div
                key={opt.value}
                className={`mcs-option p-4 border-b border-gray-100 whitespace-normal break-words leading-tight ${String(value) === String(opt.value) ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
                onClick={() => {
                  onChange({ target: { name, value: opt.value } });
                  setIsOpen(false);
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const GenerateSalary = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [holidays, setHolidays] = useState([]);
  // 💰 DYNAMIC DEDUCTIONS AND INCOMES STATE
  const [dynamicDeductions, setDynamicDeductions] = useState([]);
  const [dynamicIncomes, setDynamicIncomes] = useState([]);
  const [showDeductionForm, setShowDeductionForm] = useState(false);


  const [showIncomeForm, setShowIncomeForm] = useState(false);


  const [newDeduction, setNewDeduction] = useState({ name: '', amount: 0 });
  const [newIncome, setNewIncome] = useState({ name: '', amount: 0 });

  //  PF AND ESI STATE
  const [employeePfEsi, setEmployeePfEsi] = useState({
    pfPercentage: '',
    esicPercentage: '',
    pfAmount: 0,
    esicAmount: 0,
    loading: false,
    error: null
  });

  //  RECALCULATE TIMEOUT REF
  const recalculateTimeoutRef = React.useRef(null);

  const [formData, setFormData] = useState({
    department: '',
    company: '',
    employee: '',
    salary: 0,
    payrollMonth: '',
    totalWorkingDays: 0,
    totalHalfDays: 0,
    totalLeaves: 0,
    totalWeeklyHolidays: 0,
    totalCompanyHolidays: 0,
    totalDeductions: 0,
    totalIncomes: 0,
    netSalary: 0,
    // PF and ESI fields
    pfAmount: 0,
    esicAmount: 0,
  });
  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchCompanyData();
    fetchPayrollHistory();

    // Cleanup timeout on unmount
    return () => {
      if (recalculateTimeoutRef.current) {
        clearTimeout(recalculateTimeoutRef.current);
      }
    };
  }, []);

  // „ FILTER EMPLOYEES BY DEPARTMENT
  useEffect(() => {

    // š ONLY SHOW EMPLOYEES WHEN DEPARTMENT IS SELECTED
    if (formData.department && employees.length > 0) {
      // Find the selected department details for logging
      const selectedDept = departments.find(dept => dept._id === formData.department);
      // Filter employees by department and isActive
      const filtered = employees.filter(employee => {
        // Only include active employees
        if (employee.isActive === false) return false;
        // Handle multiple department formats and arrays
        let employeeDepartments = [];
        if (employee.department) {
          if (Array.isArray(employee.department)) {
            employeeDepartments = employee.department.map(dept => typeof dept === 'object' ? dept._id : dept);
          } else if (typeof employee.department === 'object') {
            employeeDepartments = [employee.department._id];
          } else {
            employeeDepartments = [employee.department];
          }
        }
        if (employee.departments && Array.isArray(employee.departments)) {
          const deptIds = employee.departments.map(dept => typeof dept === 'object' ? dept._id : dept);
          employeeDepartments = [...employeeDepartments, ...deptIds];
        }
        employeeDepartments = [...new Set(employeeDepartments)];
        const isMatch = employeeDepartments.includes(formData.department);
        return isMatch;
      });
      setFilteredEmployees(filtered);

      // Show user feedback
      if (filtered.length === 0) {
      } else {
      }

    } else {
      // š NO DEPARTMENT SELECTED - HIDE ALL EMPLOYEES
      setFilteredEmployees([]);
    }
  }, [employees, formData.department, departments]);

  // Add this helper function (same as in the main component for consistency)
  const getEmployeeDepartmentNames = (employee) => {
    let departmentNames = [];

    // Method 1: Check single department field (now assuming it's an array of IDs)
    if (employee.department && Array.isArray(employee.department)) {
      departmentNames = employee.department.map(deptId => {
        // deptId is an ID string; find the matching department object from the departments state
        const dept = departments.find(d => d._id === deptId);
        return dept?.departmentName || dept?.name || deptId; // Fallback to ID if not found
      }).filter(name => name); // Remove empty values
    }

    // Method 2: Check departments field (array of IDs)
    if (employee.departments && Array.isArray(employee.departments)) {
      const deptNames = employee.departments.map(deptId => {
        const dept = departments.find(d => d._id === deptId);
        return dept?.departmentName || dept?.name || deptId;
      }).filter(name => name);
      departmentNames = [...departmentNames, ...deptNames];
    }

    // Remove duplicates and filter out undefined/null values
    departmentNames = [...new Set(departmentNames.filter(name => name))];

    return departmentNames.length > 0 ? departmentNames.join(', ') : 'No Department';
  };

  // Add this function to fetch designation name(s) by ID(s) - now handles arrays
  const fetchDesignationName = async (designationIds, token) => {
    console.log("designationIds", designationIds);

    if (!designationIds || (!Array.isArray(designationIds) && typeof designationIds !== 'string')) return 'N/A';

    const ids = Array.isArray(designationIds) ? designationIds : [designationIds];
    console.log("ids-", ids);

    try {
      const promises = ids.map(async (id) => {
        if (!id || typeof id !== 'string') return 'N/A';
        const response = await axios.get(`https://greenyellow-nightingale-241896.hostingersite.com/api/designations/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Log the full response for debugging
        console.log(`Response for ID ${id}:`, response.data);

        // FIXED: Correct path based on your logs (response.data.role.name)
        return response.data?.role?.department?.name || 'N/A';
      });

      const names = await Promise.all(promises);
      console.log("Fetched designation names:", names);
      const validNames = names.filter(name => name !== 'N/A');
      return validNames.length > 0 ? validNames.join(', ') : 'N/A';
    } catch (error) {
      // IMPROVED: Log more details
      console.error('Error fetching designation:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      return 'N/A';
    }
  };



  // Update the generatePDF function to fetch and pass holidays
  const generatePDF = async (payrollId) => {
    try {

      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Authentication token not found.');
        return;
      }

      // Fetch payroll data by ID
      const payrollResponse = await axios.get(`https://greenyellow-nightingale-241896.hostingersite.com/api/payroll/${payrollId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payroll = payrollResponse.data.payroll;

      // Fetch employee and company data
      const employee = employees.find(emp => emp._id === payroll.employee._id);
      const company = selectedCompany;

      if (!employee) {
        setError('Employee data not found for PDF generation.');
        return;
      }

      // Resolve department names (handles array of IDs)
      const departmentNames = getEmployeeDepartmentNames(employee);

      // Resolve designation name(s) (handles array of IDs)
      const designationName = await fetchDesignationName(employee.designation, token);

      // Fetch company holidays for the month
      const holidays = company?._id ? await fetchHolidays(company._id) : [];

      // Prepare employee data with resolved names
      const resolvedEmployeeData = {
        ...employee,
        departmentName: departmentNames,
        designationName: designationName,
      };

      console.log("payroll", payroll);

      // Create PDF blob
      const blob = await pdf(<SalarySlipPDF payrollData={payroll} employeeData={resolvedEmployeeData} companyData={company} holidays={holidays} />).toBlob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Salary_Slip_${payroll.employee.teamMemberName}_${payroll.payrollMonth}.pdf`;

      link.click();

      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError(`Failed to generate PDF: ${error.message}${error.stack ? ' | ' + error.stack.split('\n')[0] : ''}`);
    }
  };



  // 🎯 FETCH COMPANY DATA FOR WEEKLY HOLIDAYS
  const fetchCompanyData = async () => {
    const token = localStorage.getItem('authToken');
    let companyId = localStorage.getItem('selectedCompanyId');

    if (!companyId) {
      try {
        const decoded = jwtDecode(token);
        companyId = decoded.companyId || decoded.id;
      } catch (e) {
        return;
      }
    }

    if (!companyId) return;

    try {
      const response = await axios.get(`https://greenyellow-nightingale-241896.hostingersite.com/api/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSelectedCompany(response.data);
    } catch (error) {
    }
  };

  // FETCH DEPARTMENTS FUNCTIONALITY
  const fetchDepartments = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return;
    }

    try {

      // Decode JWT token to get company ID
      const decoded = jwtDecode(token);

      // Extract company ID with multiple fallbacks
      const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || decoded.id;

      if (!companyId) {
        return;
      }

      const response = await axios.get(`https://greenyellow-nightingale-241896.hostingersite.com/api/departments/company/${companyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000,
        validateStatus: (status) => status < 500
      });


      if (response.status === 401) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return;
      }

      // Handle different response formats
      let departmentData = [];
      if (Array.isArray(response.data)) {
        departmentData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        departmentData = response.data.data;
      } else if (response.data?.departments && Array.isArray(response.data.departments)) {
        departmentData = response.data.departments;
      }


      setDepartments(departmentData);

    } catch (error) {

      if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      } else if (error.response?.status === 403) {
      } else if (error.response?.status === 404) {
        setDepartments([]);
      } else {
      }
    }
  };

  // UPDATED: fetchPayrollHistory - Use only the correct API endpoint
  const fetchPayrollHistory = async () => {
    setHistoryLoading(true);
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No access token found.');
      setHistoryLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || decoded.id;

      if (!companyId) {
        console.error('Company ID not found in token.');
        setHistoryLoading(false);
        return;
      }

      // Use the correct API endpoint: /api/payroll/ with companyId as query param
      const endpoint = `https://greenyellow-nightingale-241896.hostingersite.com/api/payroll?companyId=${companyId}`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000,
        validateStatus: (status) => status >= 200 && status < 500
      });

      if (response.status !== 200) {
        throw new Error(`Failed to fetch payroll history: ${response.status}`);
      }

      let payrollData = response.data.payrolls || response.data;  // Extract payrolls array
      if (!Array.isArray(payrollData)) {
        console.error('Unexpected response format:', response.data);
        payrollData = [];  // Fallback to empty array
      }

      console.log("payrollData", payrollData);

      const processedPayrolls = payrollData.map(payroll => {
        let departmentNames = [];
        if (Array.isArray(payroll.employee?.department)) {
          departmentNames = payroll.employee.department
            .map(dept => dept || 'Unknown Dept')  // dept is already a string (e.g., "Android")
            .filter(name => name !== 'Unknown Dept'); // Remove invalid ones if any
        } else if (payroll.employee?.department) {
          // Fallback if it's a single string (unlikely, but safe)
          departmentNames = [payroll.employee.department || 'Unknown Dept'];
        }
        const departmentString = departmentNames.length > 0 ? departmentNames.join(', ') : 'N/A';
        console.log("payroll09090909-", payroll);

        return {
          _id: payroll._id || payroll.id,
          employee: {
            teamMemberName: payroll.employee?.teamMemberName || payroll.employee?.name || 'Unknown Employee',
            name: payroll.employee?.name || payroll.employee?.teamMemberName || 'Unknown Employee',
            employeeId: payroll.employee?._id || 'N/A',
            department: {
              departmentName: departmentString,  // ✅ Now a joined string like "HR, IT"
              name: departmentString  // ✅ Same for consistency
            }
          },
          payrollMonth: payroll.payrollMonth,
          totalWorkingDays: parseInt(payroll.totalWorkingDays) || 0,
          totalHalfDays: parseInt(payroll.totalHalfDays) || 0,
          totalLeaves: parseInt(payroll.totalLeaves) || 0,
          salary: parseFloat(payroll.salary) || 0,
          grossSalary: parseFloat(payroll.grossSalary) || parseFloat(payroll.salary) || 0,
          totalDeductions: parseFloat(payroll.totalDeductions) || 0,
          totalIncomes: parseFloat(payroll.totalIncomes) || 0,
          netSalary: parseFloat(payroll.netSalary) || 0,
          deductions: payroll.deductions || [],
          incomes: payroll.incomes || [],
          createdAt: payroll.createdAt || payroll.generatedAt || new Date().toISOString(),
          status: payroll.status || 'Generated'
        };
      });

      const sortedPayrolls = processedPayrolls.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      console.log("sortedPayrolls", sortedPayrolls);

      setPayrollHistory(sortedPayrolls);
    } catch (error) {
      console.error('Error fetching payroll history:', error);
      setError(error.message || 'Failed to fetch payroll history.');
      setPayrollHistory([]);
    } finally {
      setHistoryLoading(false);
    };
  };




  // 👥 ENHANCED EMPLOYEE FETCHING WITH DEPARTMENT POPULATION
  const fetchEmployees = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      setLoading(false);
      return;
    }

    try {

      // Get company ID from localStorage (set during login/company selection)
      let companyId = localStorage.getItem('selectedCompanyId');

      // If not in localStorage, try to extract from JWT token
      if (!companyId) {
        try {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          companyId = decoded.companyId || decoded.id;
        } catch (decodeError) {
        }
      } else {
      }

      if (!companyId) {
        setError('Company information not found. Please contact administrator.');
        setLoading(false);
        return;
      }

      // Fetch employees by company ID with department population
      const response = await axios.get(`https://greenyellow-nightingale-241896.hostingersite.com/api/employees/company/${companyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: {
          populate: 'department departments', // Request department population
        },
        timeout: 15000,
        validateStatus: (status) => status < 500
      });


      if (response.status === 401) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return;
      }

      // Handle different response formats
      let employeeData = [];
      if (Array.isArray(response.data)) {
        employeeData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        employeeData = response.data.data;
      } else if (response.data?.employees && Array.isArray(response.data.employees)) {
        employeeData = response.data.employees;
      }
      console.log("employeeData5555", employeeData);


      setEmployees(employeeData);

    } catch (err) {

      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        setError('Authentication failed. Please login again.');
      } else if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to view employees.');
      } else if (err.response?.status === 404) {
        setEmployees([]);
        setError('No employees found for this company.');
      } else {
        setError('Failed to fetch company employees. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  //  FETCH EMPLOYEE PF AND ESI PERCENTAGES
  const fetchEmployeePfEsi = async (employeeId) => {
    const token = localStorage.getItem('authToken');
    if (!token || !employeeId) {
      return null;
    }

    setEmployeePfEsi(prev => ({ ...prev, loading: true, error: null }));

    try {

      const response = await axios.get(`https://greenyellow-nightingale-241896.hostingersite.com/api/employees/${employeeId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000,
        validateStatus: (status) => status < 500
      });


      if (response.status === 401) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return null;
      }

      if (response.status === 404) {
        setEmployeePfEsi(prev => ({ ...prev, loading: false, error: 'Employee not found' }));
        return null;
      }

      // Handle different response formats
      let employeeData = null;
      if (response.data) {
        if (response.data.data) {
          employeeData = response.data.data;
        } else if (response.data.employee) {
          employeeData = response.data.employee;
        } else {
          employeeData = response.data;
        }
      }


      if (employeeData) {
        // Extract PF and ESI percentages from employee data
        const pfPercentage = employeeData.pfPercentage || '';
        const esicPercentage = employeeData.esicPercentage || '';

        const pfEsiData = {
          pfPercentage: pfPercentage,
          esicPercentage: esicPercentage,
          pfAmount: 0, // Will be calculated based on salary
          esicAmount: 0, // Will be calculated based on salary
          loading: false,
          error: null
        };

        setEmployeePfEsi(pfEsiData);
        return pfEsiData;
      }

      setEmployeePfEsi(prev => ({ ...prev, loading: false, error: 'No PF/ESI data found' }));
      return null;

    } catch (error) {

      const errorMessage = error.response?.status === 401 ? 'Authentication failed' :
        error.response?.status === 403 ? 'Access denied' :
          error.response?.status === 404 ? 'Employee not found' :
            'Failed to fetch PF/ESI data';

      setEmployeePfEsi(prev => ({ ...prev, loading: false, error: errorMessage }));

      if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      }

      return null;
    }
  };

  // 💰 FETCH EMPLOYEE SALARY FUNCTIONALITY
  const fetchEmployeeSalary = async (employeeId) => {
    const token = localStorage.getItem('authToken');
    if (!token || !employeeId) {
      return null;
    }

    setSalaryLoading(true);

    try {

      const response = await axios.get(`https://greenyellow-nightingale-241896.hostingersite.com/api/employees/${employeeId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000,
        validateStatus: (status) => status < 500
      });


      if (response.status === 401) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return null;
      }

      if (response.status === 404) {
        setError('Employee not found. Please select a valid employee.');
        return null;
      }

      // Handle different response formats
      let employeeData = null;
      if (response.data) {
        if (response.data.data) {
          employeeData = response.data.data;
        } else if (response.data.employee) {
          employeeData = response.data.employee;
        } else {
          employeeData = response.data;
        }
      }


      if (employeeData) {
        // Extract salary from different possible field names
        const salary = employeeData.salary ||
          employeeData.monthlySalary ||
          employeeData.basicSalary ||
          employeeData.baseSalary ||
          0;


        return parseFloat(salary) || 0;
      }

      return 0;

    } catch (error) {

      if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      } else if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to view employee salary.');
      } else if (error.response?.status === 404) {
        setError('Employee not found. Please select a valid employee.');
      } else {
        setError('Failed to fetch employee salary. Please try again.');
      }

      return null;
    } finally {
      setSalaryLoading(false);
    }
  };

  // 🎯 FETCH COMPANY HOLIDAYS FUNCTIONALITY
  const fetchHolidays = async (companyId) => {
    const token = localStorage.getItem('authToken');
    if (!token || !companyId) return [];

    try {
      // „ Use the same endpoint as AttendanceReports.js
      const response = await axios.get(`https://greenyellow-nightingale-241896.hostingersite.com/api/holidaycreation/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
        validateStatus: (status) => status < 500
      });

      let holidayData = [];
      if (Array.isArray(response.data)) {
        holidayData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        holidayData = response.data.data;
      } else if (response.data?.holidays && Array.isArray(response.data.holidays)) {
        holidayData = response.data.holidays;
      }

      // Robust parsing and range expansion (Same as AttendanceReports.js)
      const expandedHolidays = [];

      holidayData.forEach(holiday => {
        if (!holiday) return;

        // Try to find dates in various fields
        const startStr = holiday.startDate || holiday.holidayDate || holiday.date;
        const endStr = holiday.endDate || startStr;

        if (!startStr) return;

        // Helper to parse DD/MM/YYYY or ISO
        const parseDate = (dStr) => {
          if (!dStr) return null;
          if (typeof dStr !== 'string') return new Date(dStr);

          // Regex for DD/MM/YYYY
          const ddmm = dStr.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
          if (ddmm) {
            return new Date(parseInt(ddmm[3]), parseInt(ddmm[2]) - 1, parseInt(ddmm[1]));
          }
          return new Date(dStr);
        };

        const startDate = parseDate(startStr);
        const endDate = parseDate(endStr);

        if (isNaN(startDate.getTime())) return;

        // Expand range
        const current = new Date(startDate);
        const final = isNaN(endDate.getTime()) ? startDate : endDate;

        // Safety break for 1 year
        let count = 0;
        while (current <= final && count < 366) {
          const dateKey = formatDateLocal(current);
          expandedHolidays.push({
            _id: holiday._id || holiday.id,
            name: holiday.holidayName || holiday.name || holiday.title || 'Holiday',
            date: dateKey,
            type: holiday.type || 'General',
            reason: holiday.reason || holiday.description || ''
          });
          current.setDate(current.getDate() + 1);
          count++;
        }
      });

      return expandedHolidays;
    } catch (error) {
      console.error('Error fetching holidays:', error);
      return [];
    }
  };

  // 🎯 FETCH ATTENDANCE RECORDS FOR EMPLOYEE
  // ¡ ENHANCED ATTENDANCE API FUNCTIONS (Matches AttendanceReports.js)
  const fetchEmployeeAttendance = async (employeeId, startDate, endDate) => {
    const token = localStorage.getItem('authToken');
    if (!token || !employeeId) return [];

    try {
      console.log('FETCHING ATTENDANCE FOR SUMMARY:', { employeeId, startDate, endDate });

      // Try multiple endpoints for better data retrieval (Same as AttendanceReports.js)
      let response;
      const currentCompanyId = localStorage.getItem('selectedCompanyId');

      try {
        // First try employee-specific endpoint (most reliable)
        response = await axios.get(
          `https://greenyellow-nightingale-241896.hostingersite.com/api/attendance/employee/${employeeId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
            params: currentCompanyId ? { company: currentCompanyId } : {}
          }
        );
        console.log('¡ EMPLOYEE-SPECIFIC ATTENDANCE RESPONSE:', {
          status: response.status,
          records: response.data?.length || 0
        });
      } catch (employeeErr) {
        console.warn('⚠️ Employee-specific endpoint failed, trying general endpoint:', employeeErr.message);

        // Fallback to general attendance endpoint
        response = await axios.get(`https://greenyellow-nightingale-241896.hostingersite.com/api/attendance`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          params: {
            ...(currentCompanyId ? { company: currentCompanyId } : {}),
            employee: employeeId // Filter by employee
          },
          timeout: 15000,
          validateStatus: (status) => status < 500
        });
      }

      let attendanceRecords = [];
      if (Array.isArray(response.data)) {
        attendanceRecords = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        attendanceRecords = response.data.data;
      } else if (response.data.attendance && Array.isArray(response.data.attendance)) {
        attendanceRecords = response.data.attendance;
      } else if (response.data.records && Array.isArray(response.data.records)) {
        attendanceRecords = response.data.records;
      }

      console.log('📊 TOTAL RECORDS RETRIEVED:', attendanceRecords.length);

      // Filter and map records
      const filteredRecords = attendanceRecords
        .filter(record => {
          if (!record) return false;

          // String-safe ID comparison
          const recordEmployeeId = String(record.employee?._id || record.employee || record.employeeId || '');
          const targetEmployeeId = String(employeeId);

          if (recordEmployeeId !== targetEmployeeId) return false;

          // Check date range - Use string split to avoid timezone shifts
          let recordDateString = record.date || (record.inTime ? record.inTime.split('T')[0] : null);
          if (!recordDateString) return false;

          // Ensure it's in YYYY-MM-DD format
          if (typeof recordDateString === 'string' && recordDateString.includes('T')) {
            recordDateString = recordDateString.split('T')[0];
          }

          return recordDateString >= startDate && recordDateString <= endDate;
        })
        .map(record => {
          // Calculate work hours if not present
          let calculatedHours = record.hours || '0:00';
          let workingTimeMinutes = record.workingTime || 0;

          // If we have inTime and outTime but no hours, calculate it
          if (record.inTime && record.outTime && (!record.hours || record.hours === '0:00')) {
            try {
              calculatedHours = calculateEnhancedWorkingTime(record.inTime, record.outTime);
            } catch (e) {
            }
          }

          // Robust date extraction
          let recordDate = record.date || (record.inTime ? record.inTime.split('T')[0] : null);
          if (recordDate && typeof recordDate === 'string' && recordDate.includes('T')) {
            recordDate = recordDate.split('T')[0];
          } else if (recordDate && typeof recordDate === 'string' && recordDate.includes('/')) {
            const parts = recordDate.split('/');
            if (parts.length === 3) {
              recordDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
          }

          return {
            ...record,
            _id: record._id || record.id,
            date: recordDate,
            inTime: record.inTime,
            outTime: record.outTime,
            workingTime: workingTimeMinutes,
            hours: calculatedHours,
            status: record.status || 'Present'
          };
        });



      console.log('🎯 FILTERED RECORDS FOR MONTH:', filteredRecords.length);
      return filteredRecords;
    } catch (error) {
      console.error('❌ Error fetching employee attendance for summary:', error);
      return [];
    }
  };


  // 🎯 CALCULATE ATTENDANCE SUMMARY (Similar to DailyAttendance.js)
  const calculateAttendanceSummary = async (employeeId, payrollMonth) => {
    if (!employeeId || !payrollMonth) {
      return { totalWorkingDays: 0, totalHalfDays: 0, totalLeaves: 0, totalWeeklyHolidays: 0, totalCompanyHolidays: 0 };
    }

    setAttendanceLoading(true);

    try {
      // Parse month (YYYY-MM format)
      const [year, month] = payrollMonth.split('-').map(Number);
      const monthIndex = month - 1; // JavaScript months are 0-indexed

      // Get date range for the month - Avoid toISOString which shifts dates (Matches AttendanceReports.js)
      const startDateObj = new Date(year, monthIndex, 1);
      const endDateObj = new Date(year, monthIndex + 1, 0);
      const startDate = formatDateLocal(startDateObj);
      const endDate = formatDateLocal(endDateObj);
      const daysInMonth = endDateObj.getDate();

      // Get company ID for holidays
      let companyId = localStorage.getItem('selectedCompanyId');
      if (!companyId) {
        const token = localStorage.getItem('authToken');
        try {
          const decoded = jwtDecode(token);
          companyId = decoded.companyId || decoded.id;
        } catch (e) {
        }
      }

      // Fetch attendance records and holidays
      const [attendanceRecords, holidayData] = await Promise.all([
        fetchEmployeeAttendance(employeeId, startDate, endDate),
        fetchHolidays(companyId)
      ]);

      // Filter holidays for the selected month
      const monthHolidays = holidayData.filter(holiday => {
        const holidayDate = new Date(holiday.date);
        return holidayDate.getFullYear() === year && holidayDate.getMonth() === monthIndex;
      });

      // Get employee data for joining date and weekly holidays
      const selectedEmployee = employees.find(emp => emp._id === employeeId);
      let joiningDate = null;
      let weeklyHolidays = [];

      if (selectedEmployee) {
        // Get joining date
        if (selectedEmployee.dateOfJoining) {
          joiningDate = new Date(selectedEmployee.dateOfJoining);
        } else if (selectedEmployee.joiningDate) {
          joiningDate = new Date(selectedEmployee.joiningDate);
        }

        weeklyHolidays = selectedEmployee.weeklyHoliday || [];

      }
      // Convert day names to numbers
      weeklyHolidays = weeklyHolidays
        .map(day => {
          if (typeof day === 'string') {
            const dayMap = {
              'sunday': 0, 'sun': 0,
              'monday': 1, 'mon': 1,
              'tuesday': 2, 'tue': 2,
              'wednesday': 3, 'wed': 3,
              'thursday': 4, 'thu': 4,
              'friday': 5, 'fri': 5,
              'saturday': 6, 'sat': 6
            };
            return dayMap[day.toLowerCase()];
          }
          return day;
        })
        .filter(day => day !== undefined);


      // Initialize counters
      let totalWorkingDays = 0;
      let totalHalfDays = 0;
      let totalLeaves = 0;
      let totalWeeklyHolidays = 0;
      let totalCompanyHolidays = 0;

      // —ï Map attendance records by date for fast lookup
      const attendanceMap = {};
      attendanceRecords.forEach(record => {
        // Ensure we extract a clean YYYY-MM-DD date from the record
        let recordDate = record.date || (record.inTime ? record.inTime.split('T')[0] : null);
        if (recordDate && typeof recordDate === 'string' && recordDate.includes('T')) {
          recordDate = recordDate.split('T')[0];
        }

        if (recordDate) {
          attendanceMap[recordDate] = record;
        }
      });

      // … Iterate through every day of the month (Similar to AttendanceReports.js)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Fetch employee shift info for accurate status calculation
      let employeeShiftInfo = null;
      try {
        const token = localStorage.getItem('authToken');
        const empResponse = await axios.get(`https://greenyellow-nightingale-241896.hostingersite.com/api/employees/${employeeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const employee = empResponse.data;

        // Basic shift info extraction
        employeeShiftInfo = {
          shiftStartTime: employee.shiftStartTime || '09:00',
          shiftEndTime: employee.shiftEndTime || '18:00',
          workingHours: employee.workingHours || 8
        };

        // If has a shift object, try to use it
        if (employee.shift && typeof employee.shift === 'object') {
          employeeShiftInfo.shiftStartTime = employee.shift.startTime || employeeShiftInfo.shiftStartTime;
          employeeShiftInfo.shiftEndTime = employee.shift.endTime || employeeShiftInfo.shiftEndTime;
        }
      } catch (e) {
        console.warn("Failed to fetch shift info for summary:", e);
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, monthIndex, day);
        const dateStr = formatDateLocal(currentDate);

        // A. Check for Future Dates (Priority 0 - Matches AttendanceReports.js)
        if (currentDate > today) {
          // Future dates don't count towards any category in summary yet
          continue;
        }

        // B. Check for attendance record (Priority 1 - Matches AttendanceReports.js)
        const record = attendanceMap[dateStr];
        if (record) {
          // Use shift-based status calculation to match AttendanceReports.js
          const shiftStatus = calculateShiftBasedStatus(record, employeeShiftInfo);
          const statusText = shiftStatus.status;

          // Enhanced check to ensure "Present" is counted even if status is lowercase or slightly different
          const dbStatus = (record.status || '').toLowerCase();
          const isPresent = statusText === 'Present' ||
            statusText === 'Late' ||
            statusText === 'Incomplete' ||
            dbStatus.includes('present') ||
            dbStatus === 'p' ||
            dbStatus === 'completed' ||
            dbStatus === 'working';

          if (isPresent) {
            totalWorkingDays++;
          } else if (statusText === 'Half Day' || dbStatus.includes('half')) {
            totalHalfDays++;
          } else {
            totalLeaves++;
          }
          continue;
        }

        // C. Check if it's a company holiday (Priority 2)
        const companyHoliday = monthHolidays.find(h => h.date === dateStr);
        if (companyHoliday) {
          totalCompanyHolidays++;
          continue;
        }

        // D. Check if it's a weekly holiday (Priority 3)
        const isWeeklyHoliday = weeklyHolidays.includes(currentDate.getDay());
        if (isWeeklyHoliday) {
          totalWeeklyHolidays++;
          continue;
        }

        // E. No record and No holiday - Check if past
        if (currentDate < today) {
          // If past joining date, count as absent
          if (!joiningDate || currentDate >= joiningDate) {
            totalLeaves++;
          }
        }
      }


      return { totalWorkingDays, totalHalfDays, totalLeaves, totalWeeklyHolidays, totalCompanyHolidays };

    } catch (error) {
      return { totalWorkingDays: 0, totalHalfDays: 0, totalLeaves: 0, totalWeeklyHolidays: 0, totalCompanyHolidays: 0 };
    } finally {
      setAttendanceLoading(false);
    }
  };

  // 💰 CALCULATE PROPORTIONAL SALARY BASED ON ATTENDANCE (Including Holiday Pay)
  const calculateProportionalSalary = (baseSalary, totalWorkingDays, totalHalfDays, payrollMonth, totalWeeklyHolidays = 0, totalCompanyHolidays = 0) => {
    if (!baseSalary || !payrollMonth) {
      return {
        grossSalary: 0,
        perDayRate: 0,
        effectiveWorkingDays: 0,
        totalMonthDays: 0,
        deductionDays: 0,
        paidHolidayDays: 0
      };
    }

    try {
      // Parse month to get total days in that month
      const [year, month] = payrollMonth.split('-').map(Number);
      const totalMonthDays = new Date(year, month, 0).getDate(); // Get days in month

      // Calculate per-day salary rate
      const perDayRate = baseSalary / totalMonthDays;

      // Calculate effective working days
      // Full days = totalWorkingDays
      // Half days = totalHalfDays * 0.5 (half value)
      const effectiveWorkingDays = totalWorkingDays + (totalHalfDays * 0.5);

      // Calculate paid holiday days (holidays are paid days)
      const paidHolidayDays = totalWeeklyHolidays + totalCompanyHolidays;

      // Calculate total paid days (working days + holiday days)
      const totalPaidDays = effectiveWorkingDays + paidHolidayDays;

      // Calculate deduction days (only actual unpaid absent days)
      const deductionDays = totalMonthDays - totalPaidDays;

      // Calculate gross salary based on total paid days (including holidays)
      const grossSalary = totalPaidDays * perDayRate;


      return {
        grossSalary: Math.round(grossSalary * 100) / 100, // Round to 2 decimal places
        perDayRate: Math.round(perDayRate * 100) / 100,
        effectiveWorkingDays: effectiveWorkingDays,
        totalMonthDays: totalMonthDays,
        deductionDays: deductionDays,
        paidHolidayDays: paidHolidayDays,
        totalPaidDays: totalPaidDays,
        workingDaysPay: Math.round((effectiveWorkingDays * perDayRate) * 100) / 100,
        holidayPay: Math.round((paidHolidayDays * perDayRate) * 100) / 100
      };

    } catch (error) {
      return {
        grossSalary: baseSalary, // Fallback to base salary
        perDayRate: 0,
        effectiveWorkingDays: 0,
        totalMonthDays: 0,
        deductionDays: 0,
        paidHolidayDays: 0,
        totalPaidDays: 0,
        workingDaysPay: 0,
        holidayPay: 0
      };
    }
  };

  // Removed fetchPayrolls function since /api/payroll endpoint doesn't exist
  // We'll only use the specific employee/month endpoint when needed

  // 🎯 DYNAMIC DEDUCTIONS MANAGEMENT FUNCTIONS
  const addDeduction = () => {
    if (!newDeduction.name.trim() || newDeduction.amount <= 0) {
      setError('Please enter a valid deduction name and amount.');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const deduction = {
      id: Date.now() + Math.random(),
      name: newDeduction.name.trim(),
      amount: parseFloat(newDeduction.amount) || 0
    };

    setDynamicDeductions(prev => [...prev, deduction]);
    setNewDeduction({ name: '', amount: 0 });
    setShowDeductionForm(false);

    // Recalculate totals
    recalculateTotals([...dynamicDeductions, deduction], dynamicIncomes);

    setSuccess(`✅ Deduction "${deduction.name}" added successfully!`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const removeDeduction = (id) => {
    const updatedDeductions = dynamicDeductions.filter(item => item.id !== id);
    setDynamicDeductions(updatedDeductions);

    // Recalculate totals
    recalculateTotals(updatedDeductions, dynamicIncomes);

    setSuccess('✅ Deduction removed successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  // 🎯 DYNAMIC INCOMES MANAGEMENT FUNCTIONS
  const addIncome = () => {
    if (!newIncome.name.trim() || newIncome.amount <= 0) {
      setError('Please enter a valid income name and amount.');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const income = {
      id: Date.now() + Math.random(),
      name: newIncome.name.trim(),
      amount: parseFloat(newIncome.amount) || 0
    };

    setDynamicIncomes(prev => [...prev, income]);
    setNewIncome({ name: '', amount: 0 });
    setShowIncomeForm(false);

    // Recalculate totals
    recalculateTotals(dynamicDeductions, [...dynamicIncomes, income]);

    setSuccess(`✅ Income "${income.name}" added successfully!`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const removeIncome = (id) => {
    const updatedIncomes = dynamicIncomes.filter(item => item.id !== id);
    setDynamicIncomes(updatedIncomes);

    // Recalculate totals
    recalculateTotals(dynamicDeductions, updatedIncomes);

    setSuccess('✅ Income removed successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  //  CALCULATE PF AND ESI AMOUNTS
  const calculatePfEsiAmounts = (pfBaseSalary, esiBaseSalary, pfPercentage, esicPercentage) => {
    const pfAmount = pfPercentage ? (pfBaseSalary * parseFloat(pfPercentage)) / 100 : 0;
    const esicAmount = esicPercentage ? (esiBaseSalary * parseFloat(esicPercentage)) / 100 : 0;

    return {
      pfAmount: Math.round(pfAmount * 100) / 100, // Round to 2 decimal places
      esicAmount: Math.round(esicAmount * 100) / 100
    };
  };

  const recalculateTotals = React.useCallback((deductions = dynamicDeductions, incomes = dynamicIncomes) => {
    // Clear any existing timeout to debounce rapid calls
    if (recalculateTimeoutRef.current) {
      clearTimeout(recalculateTimeoutRef.current);
    }

    recalculateTimeoutRef.current = setTimeout(() => {
      const totalDeductions = deductions.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      const totalIncomes = incomes.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

      setFormData(prev => {
        const baseSalary = parseFloat(prev.salary) || 0;
        const proportionalCalc = calculateProportionalSalary(
          baseSalary,
          prev.totalWorkingDays,
          prev.totalHalfDays,
          prev.payrollMonth,
          prev.totalWeeklyHolidays || 0,
          prev.totalCompanyHolidays || 0
        );

        const grossSalary = proportionalCalc.grossSalary;

        // Calculate PF based on gross salary, ESI based on base salary
        const pfEsiCalc = calculatePfEsiAmounts(
          grossSalary,
          baseSalary,
          employeePfEsi.pfPercentage,
          employeePfEsi.esicPercentage
        );

        // Total deductions include dynamic deductions + PF + ESI
        const totalDeductionsWithPfEsi = totalDeductions + pfEsiCalc.pfAmount + pfEsiCalc.esicAmount;

        const netSalary = grossSalary + totalIncomes - totalDeductionsWithPfEsi;

        return {
          ...prev,
          totalDeductions,
          totalIncomes,
          pfAmount: pfEsiCalc.pfAmount,
          esicAmount: pfEsiCalc.esicAmount,
          netSalary: Math.max(0, netSalary)
        };
      });
    }, 50); // 50ms debounce
  }, [dynamicDeductions, dynamicIncomes, employeePfEsi.pfPercentage, employeePfEsi.esicPercentage]);


  // „ RESET FORM FUNCTION (Updated)
  const resetForm = () => {
    setFormData({
      department: '',
      company: '',
      employee: '',
      salary: 0,
      payrollMonth: '',
      totalWorkingDays: 0,
      totalHalfDays: 0,
      totalLeaves: 0,
      totalDeductions: 0,
      totalIncomes: 0,
      netSalary: 0,
      pfAmount: 0,
      esicAmount: 0,
    });

    // Reset dynamic arrays
    setDynamicDeductions([]);
    setDynamicIncomes([]);
    setShowDeductionForm(false);
    setShowIncomeForm(false);
    setNewDeduction({ name: '', amount: 0 });
    setNewIncome({ name: '', amount: 0 });

    // Reset PF/ESI data
    setEmployeePfEsi({
      pfPercentage: '',
      esicPercentage: '',
      pfAmount: 0,
      esicAmount: 0,
      loading: false,
      error: null
    });

    setError('');
    setSuccess('');
    setShowForm(false);
  };

  const handleInputChange = async (e) => {
    const { name, value, type, checked } = e.target;

    // Handle dynamic form fields (no more nested deductions/incomes)
    {
      let processedValue = value;

      // Handle different field types
      if (name.includes('total') || name.includes('Days') || name.includes('Leaves')) {
        processedValue = parseInt(value) || 0;
      } else if (name === 'salary' || name === 'totalDeductions' || name === 'totalIncomes' || name === 'netSalary') {
        processedValue = parseFloat(value) || 0;
      }

      let newFormData = {
        ...formData,
        [name]: processedValue
      };

      // ENHANCED DEPARTMENT CHANGE HANDLING
      if (name === 'department') {
        const selectedDept = departments.find(dept => dept._id === processedValue);

        // Reset employee selection and salary when department changes
        newFormData.employee = '';
        newFormData.salary = 0;

        // Reset attendance fields as well since employee changed
        newFormData.totalWorkingDays = 0;
        newFormData.totalHalfDays = 0;
        newFormData.totalLeaves = 0;
        newFormData.netSalary = 0;

        // Show user feedback about department change
        if (processedValue) {
          setSuccess(`Department selected: ${selectedDept?.departmentName || selectedDept?.name}. Now you can select an employee from this department.`);
        } else {
          setSuccess('š Department cleared. Please select a department to view employees.');
        }
        setTimeout(() => setSuccess(''), 3000);
      }

      // Fetch employee salary and PF/ESI when employee is selected
      if (name === 'employee' && processedValue) {

        // Fetch salary and PF/ESI data concurrently
        Promise.all([
          fetchEmployeeSalary(processedValue),
          fetchEmployeePfEsi(processedValue)
        ]).then(([salary, pfEsiData]) => {
          if (salary !== null) {
            setFormData(prev => {
              const updatedData = {
                ...prev,
                salary: salary
              };

              // Recalculate net salary with new base salary using dynamic arrays
              recalculateTotals();

              return updatedData;
            });

            let successMessage = `✅ Employee salary loaded: ₹${salary.toLocaleString('en-IN')}`;

            if (pfEsiData && (pfEsiData.pfPercentage || pfEsiData.esicPercentage)) {
              successMessage += `\n PF/ESI percentages loaded: PF ${pfEsiData.pfPercentage || 'N/A'}%, ESI ${pfEsiData.esicPercentage || 'N/A'}%`;
            }

            setSuccess(successMessage);
            setTimeout(() => setSuccess(''), 5000);
          }
        }).catch(error => {
          setError('Failed to fetch employee data. Please enter manually.');
          setTimeout(() => setError(''), 5000);
        });
      }

      // Auto-calculate net salary when salary or attendance fields change
      if (name === 'salary' || name === 'totalWorkingDays' || name === 'totalHalfDays' || name === 'totalLeaves') {
        // Recalculate using dynamic arrays
        recalculateTotals();
      }

      setFormData(newFormData);

      // Auto-calculate attendance when both employee and month are selected
      if ((name === 'employee' || name === 'payrollMonth') &&
        newFormData.employee && newFormData.payrollMonth) {

        try {
          const attendanceSummary = await calculateAttendanceSummary(
            newFormData.employee,
            newFormData.payrollMonth
          );

          setFormData(prev => {
            const updatedData = {
              ...prev,
              totalWorkingDays: attendanceSummary.totalWorkingDays,
              totalHalfDays: attendanceSummary.totalHalfDays,
              totalLeaves: attendanceSummary.totalLeaves,
              totalWeeklyHolidays: attendanceSummary.totalWeeklyHolidays,
              totalCompanyHolidays: attendanceSummary.totalCompanyHolidays
            };

            // Recalculate net salary with new attendance data using dynamic arrays
            recalculateTotals();

            return updatedData;
          });

          setSuccess(`✅ Attendance calculated: 
          📊 ${attendanceSummary.totalWorkingDays} Working Days 
          • ${attendanceSummary.totalHalfDays} Half Days 
          ❌ ${attendanceSummary.totalLeaves} Leaves (excluding holidays)
          … ${attendanceSummary.totalWeeklyHolidays} Weekly Holidays
          🎉 ${attendanceSummary.totalCompanyHolidays} Company Holidays`);
          setTimeout(() => setSuccess(''), 5000);

        } catch (error) {
          setError('Failed to calculate attendance automatically. Please enter values manually.');
          setTimeout(() => setError(''), 5000);
        }
      }
    }
  };

  const handleGeneratePayroll = async (e) => {
    e.preventDefault();
    setIsGenerating(true);
    setError('');
    setSuccess('');

    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('Access token not found. Please login.');
      setIsGenerating(false);
      return;
    }

    // Validate required fields
    if (!formData.employee) {
      setError('Please select an employee.');
      setIsGenerating(false);
      return;
    }

    if (!formData.payrollMonth) {
      setError('Please select a payroll month.');
      setIsGenerating(false);
      return;
    }

    // Get company ID from JWT token
    let companyId;
    try {
      const decoded = jwtDecode(token);
      companyId = decoded.companyId || decoded.company?._id || decoded.orgId || decoded.id;
    } catch (error) {
      setError('Invalid authentication token. Please login again.');
      setIsGenerating(false);
      return;
    }

    // Fallback to localStorage if not in token
    if (!companyId) {
      companyId = localStorage.getItem('selectedCompanyId');
    }

    if (!companyId) {
      setError('Company information not found. Please contact administrator.');
      setIsGenerating(false);
      return;
    }

    // Ensure all numeric values are properly formatted and use dynamic arrays
    const payload = {
      companyId: formData.company || companyId,
      employeeId: formData.employee,
      salary: parseFloat(formData.salary) || 0,
      payrollMonth: formData.payrollMonth,
      totalWorkingDays: parseInt(formData.totalWorkingDays) || 0,
      totalHalfDays: parseInt(formData.totalHalfDays) || 0,
      totalLeaves: parseInt(formData.totalLeaves) || 0,
      // 💰 DYNAMIC DEDUCTIONS AND INCOMES
      deductions: dynamicDeductions.map(item => ({
        type: item.name,
        amount: parseFloat(item.amount) || 0
      })),
      incomes: dynamicIncomes.map(item => ({
        type: item.name,
        amount: parseFloat(item.amount) || 0
      })),
      totalDeductions: parseFloat(formData.totalDeductions) || 0,
      totalIncomes: parseFloat(formData.totalIncomes) || 0,
      //  PF AND ESI AMOUNTS
      pfAmount: parseFloat(formData.pfAmount) || 0,
      esicAmount: parseFloat(formData.esicAmount) || 0,
      pfPercentage: employeePfEsi.pfPercentage || '',
      esicPercentage: employeePfEsi.esicPercentage || '',
      netSalary: parseFloat(formData.netSalary) || 0,
    };

    try {
      const response = await axios.post(
        'https://greenyellow-nightingale-241896.hostingersite.com/api/payroll/generate',
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSuccess('Payroll generated successfully!');

      // Refresh payroll history after successful generation
      await fetchPayrollHistory();

      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {

      // More detailed error handling
      if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Invalid data provided. Please check all fields.';
        setError(`Validation Error: ${errorMsg}`);
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please login again.');
      } else if (error.response?.status === 403) {
        setError('You do not have permission to generate payroll.');
      } else {
        setError(error.response?.data?.message || 'Error generating payroll. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };



  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-teal-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payroll data...</p>
        </div>
      </div>
    );
  }

  const mobileStyles = `
    @media (max-width: 768px) {
      select {
        text-overflow: ellipsis;
        padding-right: 2rem !important;
        appearance: none;
        background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%233b82f6%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
        background-repeat: no-repeat;
        background-position: right 1rem center;
        background-size: 0.75rem auto;
      }
      
      .desktop-select { display: none !important; }
      .mobile-custom-select { display: block !important; position: relative; width: 100%; }

      /* Overall compactness overrides */
      #generate-salary-outer-wrapper {
        padding-left: 0px !important;
        padding-right: 0px !important;
        padding-top: 0.25rem !important;
        padding-bottom: 0.25rem !important;
        background: transparent !important;
      }
      .max-w-full.mx-auto {
        padding-left: 0px !important;
        padding-right: 0px !important;
      }
      
      /* Card margins and padding reductions - Edge to Edge */
      .rounded-3xl {
        border-radius: 0px !important;
        border-left: none !important;
        border-right: none !important;
        padding: 0.75rem 0.5rem !important;
        margin-left: 0px !important;
        margin-right: 0px !important;
        margin-bottom: 0.5rem !important;
      }
      .rounded-2xl {
        border-radius: 0px !important;
        border-left: none !important;
        border-right: none !important;
        padding: 0.75rem 0.5rem !important;
        margin-left: 0px !important;
        margin-right: 0px !important;
        margin-bottom: 0.35rem !important;
      }
      .p-6 {
        padding: 0.75rem 0.5rem !important;
      }
      .p-8 {
        padding: 0.75rem 0.5rem !important;
      }
      .mb-6 {
        margin-bottom: 0.5rem !important;
      }
      .mb-8 {
        margin-bottom: 0.5rem !important;
      }
      .space-y-8 > :not([hidden]) ~ :not([hidden]) {
        margin-top: 0.75rem !important;
      }
      .space-y-6 > :not([hidden]) ~ :not([hidden]) {
        margin-top: 0.5rem !important;
      }
      .gap-6 {
        gap: 0.75rem !important;
      }
      
      /* Typography compression */
      .text-3xl {
        font-size: 1.3rem !important;
        line-height: 1.25 !important;
      }
      .text-2xl {
        font-size: 1.125rem !important;
        line-height: 1.25 !important;
      }
      .text-xl {
        font-size: 1rem !important;
        line-height: 1.2 !important;
      }
      
      /* Icon size overrides */
      .w-8.h-8 {
        width: 1.75rem !important;
        height: 1.75rem !important;
      }
      .w-10.h-10 {
        width: 2rem !important;
        height: 2rem !important;
      }
      .w-16.h-16 {
        width: 3rem !important;
        height: 3rem !important;
      }
      
      /* Form fields styling */
      input, select, .mcs-trigger {
        padding-top: 0.5rem !important;
        padding-bottom: 0.5rem !important;
        padding-left: 0.75rem !important;
        padding-right: 0.75rem !important;
        border-radius: 8px !important;
        font-size: 0.85rem !important;
      }
      .mcs-label {
        font-size: 0.85rem !important;
      }
      label {
        font-size: 0.75rem !important;
      }
      
      /* Attendance Summary Cards Grid conversion to 2x2 */
      .bg-gradient-to-br.from-cyan-50.to-blue-100 .grid {
        display: grid !important;
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        gap: 0.5rem !important;
      }
      .bg-gradient-to-br.from-cyan-50.to-blue-100 .grid > div {
        padding: 0.5rem !important;
        border-radius: 8px !important;
      }
      .bg-gradient-to-br.from-cyan-50.to-blue-100 .grid > div .w-12.h-12 {
        width: 1.75rem !important;
        height: 1.75rem !important;
        font-size: 0.875rem !important;
        margin-bottom: 0.25rem !important;
      }
      .bg-gradient-to-br.from-cyan-50.to-blue-100 .grid > div .text-2xl {
        font-size: 1.1rem !important;
        margin-bottom: 0 !important;
        font-weight: 700 !important;
      }
      .bg-gradient-to-br.from-cyan-50.to-blue-100 .grid > div .text-xs.font-semibold {
        font-size: 0.7rem !important;
        margin-bottom: 0 !important;
      }
      .bg-gradient-to-br.from-cyan-50.to-blue-100 .grid > div .text-xs.text-gray-500 {
        display: none !important; /* Hide description to optimize screen height */
      }
      
      /* Financial adjustment cards empty state */
      .py-8 {
        padding-top: 0.75rem !important;
        padding-bottom: 0.75rem !important;
      }
      .py-8 .w-12.h-12 {
        width: 1.5rem !important;
        height: 1.5rem !important;
        margin-bottom: 0.125rem !important;
      }
      .py-8 .text-sm {
        font-size: 0.75rem !important;
      }
      .py-8 .text-xs {
        font-size: 0.65rem !important;
      }
      
      /* Financial adjustment added rows list */
      .bg-gradient-to-br.from-red-50.to-orange-100 .space-y-4 > div,
      .bg-gradient-to-br.from-green-50.to-emerald-100 .space-y-4 > div {
        padding: 0.4rem 0.6rem !important;
        border-radius: 8px !important;
      }
      .bg-gradient-to-br.from-red-50.to-orange-100 .space-y-4 .text-lg,
      .bg-gradient-to-br.from-green-50.to-emerald-100 .space-y-4 .text-lg {
        font-size: 0.75rem !important;
      }
      .bg-gradient-to-br.from-red-50.to-orange-100 .space-y-4 .text-sm,
      .bg-gradient-to-br.from-green-50.to-emerald-100 .space-y-4 .text-sm {
        font-size: 0.65rem !important;
      }
      .bg-gradient-to-br.from-red-50.to-orange-100 .space-y-4 .text-xl,
      .bg-gradient-to-br.from-green-50.to-emerald-100 .space-y-4 .text-xl {
        font-size: 0.85rem !important;
        margin-right: 0.35rem !important;
      }
      .bg-gradient-to-br.from-red-50.to-orange-100 .space-y-4 button,
      .bg-gradient-to-br.from-green-50.to-emerald-100 .space-y-4 button {
        padding: 0.2rem !important;
      }
      
      /* Financial adjustment totals bar */
      .bg-gradient-to-br.from-red-50.to-orange-100 .mt-6.pt-4,
      .bg-gradient-to-br.from-green-50.to-emerald-100 .mt-6.pt-4 {
        margin-top: 0.5rem !important;
        padding-top: 0.5rem !important;
      }
      .bg-gradient-to-br.from-red-50.to-orange-100 .mt-6.pt-4 .text-lg,
      .bg-gradient-to-br.from-green-50.to-emerald-100 .mt-6.pt-4 .text-lg {
        font-size: 0.8rem !important;
      }
      .bg-gradient-to-br.from-red-50.to-orange-100 .mt-6.pt-4 .text-2xl,
      .bg-gradient-to-br.from-green-50.to-emerald-100 .mt-6.pt-4 .text-2xl {
        font-size: 1rem !important;
      }

      /* Adjustments input sub-forms */
      .mcs-trigger {
        padding-top: 0.5rem !important;
        padding-bottom: 0.5rem !important;
        padding-left: 0.75rem !important;
        padding-right: 0.75rem !important;
        border-radius: 8px !important;
        font-size: 0.85rem !important;
      }
      .mcs-label {
        font-size: 0.85rem !important;
      }
      .mb-6.p-4.bg-white\/80 {
        margin-bottom: 0.5rem !important;
        padding: 0.5rem !important;
        border-radius: 10px !important;
      }
      .mb-6.p-4.bg-white\/80 .mcs-trigger {
        padding-top: 0.4rem !important;
        padding-bottom: 0.4rem !important;
        font-size: 0.75rem !important;
      }
      .mb-6.p-4.bg-white\/80 .mcs-label {
        font-size: 0.75rem !important;
      }
      .mb-6.p-4.bg-white\/80 .text-lg {
        font-size: 0.85rem !important;
        margin-bottom: 0.5rem !important;
      }
      .mb-6.p-4.bg-white\/80 .space-y-4 > div {
        margin-top: 0.35rem !important;
      }
      .mb-6.p-4.bg-white\/80 button {
        padding-top: 0.4rem !important;
        padding-bottom: 0.4rem !important;
        font-size: 0.75rem !important;
      }

      /* Final Salary Calculation Grid side-by-side */
      .bg-gradient-to-br.from-yellow-50.to-amber-100 .grid {
        display: grid !important;
        grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        gap: 0.3rem !important;
        margin-bottom: 0.5rem !important;
      }
      .bg-gradient-to-br.from-yellow-50.to-amber-100 .grid > div {
        padding: 0.4rem 0.2rem !important;
        border-radius: 8px !important;
      }
      .bg-gradient-to-br.from-yellow-50.to-amber-100 .grid > div .text-3xl {
        font-size: 0.875rem !important;
        margin-bottom: 0 !important;
        font-weight: 700 !important;
      }
      .bg-gradient-to-br.from-yellow-50.to-amber-100 .grid > div .font-semibold {
        font-size: 0.65rem !important;
      }
      .bg-gradient-to-br.from-yellow-50.to-amber-100 .grid > div .text-xs.text-gray-500 {
        display: none !important;
      }

      /* PF & ESI Section side-by-side */
      .bg-gradient-to-br.from-orange-50.to-red-100 .grid {
        display: grid !important;
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        gap: 0.4rem !important;
      }
      .bg-gradient-to-br.from-orange-50.to-red-100 .grid > div {
        padding: 0.4rem !important;
        border-radius: 8px !important;
      }
      .bg-gradient-to-br.from-orange-50.to-red-100 .grid > div .text-sm.font-medium {
        font-size: 0.65rem !important;
      }
      .bg-gradient-to-br.from-orange-50.to-red-100 .grid > div .text-xs.bg-orange-100,
      .bg-gradient-to-br.from-orange-50.to-red-100 .grid > div .text-xs.bg-red-100 {
        font-size: 0.6rem !important;
        padding: 0.05rem 0.25rem !important;
      }
      .bg-gradient-to-br.from-orange-50.to-red-100 .grid > div .text-2xl {
        font-size: 0.95rem !important;
        font-weight: 700 !important;
      }
      
      /* Hide decorative badges/info in header */
      .flex.flex-wrap.items-center.gap-2.md\:gap-4 {
        display: none !important;
      }

      /* PF/ESI Info alert block */
      .bg-gradient-to-br.from-orange-50.to-red-100 .mt-4.p-4 {
        margin-top: 0.35rem !important;
        padding: 0.35rem !important;
        border-radius: 6px !important;
      }
      .bg-gradient-to-br.from-orange-50.to-red-100 .mt-4.p-4 .text-sm {
        font-size: 0.65rem !important;
        line-height: 1.2 !important;
      }
      .bg-gradient-to-br.from-orange-50.to-red-100 .mt-4.p-4 svg {
        width: 0.85rem !important;
        height: 0.85rem !important;
        margin-right: 0.25rem !important;
      }

      /* Net Salary Display */
      .border-2.border-yellow-300 {
        padding: 0.6rem !important;
        border-radius: 12px !important;
      }
      .border-2.border-yellow-300 .text-lg {
        font-size: 0.75rem !important;
        margin-bottom: 0.125rem !important;
      }
      .border-2.border-yellow-300 .text-5xl {
        font-size: 1.6rem !important;
        margin-bottom: 0.125rem !important;
      }
      .border-2.border-yellow-300 .text-sm.text-gray-500 {
        font-size: 0.65rem !important;
      }

      /* Buttons & Actions padding adjustments */
      button[type="submit"] {
        padding: 0.5rem 1.5rem !important;
        border-radius: 8px !important;
        font-size: 0.85rem !important;
      }
      button[type="submit"] svg {
        width: 1rem !important;
        height: 1rem !important;
        margin-right: 0.25rem !important;
      }
      
      button.px-4.py-2.bg-blue-100,
      button.px-4.py-2.bg-red-100,
      button.px-4.py-2.bg-green-100 {
        padding: 0.25rem 0.5rem !important;
        border-radius: 6px !important;
        font-size: 0.7rem !important;
      }
      button.px-4.py-2.bg-blue-100 svg,
      button.px-4.py-2.bg-red-100 svg,
      button.px-4.py-2.bg-green-100 svg {
        width: 0.75rem !important;
        height: 0.75rem !important;
        margin-right: 0.15rem !important;
      }

      /* Action Buttons */
      #generate-salary-btn {
        width: auto !important;
        margin: 0.25rem auto !important;
        padding: 0.6rem 1.5rem !important;
        font-size: 0.85rem !important;
        border-radius: 9999px !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2) !important;
      }
      #refresh-history-btn {
        padding: 0.45rem 0.75rem !important;
        font-size: 0.75rem !important;
        border-radius: 9999px !important;
      }

      /* Badge list mobile styles */
      #payroll-history-badges {
        display: flex !important;
        flex-wrap: wrap !important;
        gap: 0.35rem !important;
        margin-top: 0.25rem !important;
      }

      /* Payroll History Card rows */
      table { display: block !important; width: 100% !important; }
      thead { display: none !important; }
      tbody { display: block !important; }
      tr {
        display: flex !important;
        flex-direction: column !important;
        background: #ffffff !important;
        border-radius: 12px !important;
        margin-bottom: 0.5rem !important;
        box-shadow: 0 1px 4px rgba(0,0,0,0.02) !important;
        border: 1px solid #f1f5f9 !important;
        padding: 0 !important;
        overflow: hidden !important;
      }
      td {
        display: flex !important;
        justify-content: space-between !important;
        align-items: center !important;
        padding: 0.4rem 0.65rem !important;
        border-bottom: 1px solid #f8fafc !important;
        text-align: right !important;
      }
      td:last-child { 
        border-bottom: none !important; 
        background: #f8fafc !important; 
        padding: 0.5rem 0.65rem !important; 
      }
      td::before {
        content: attr(data-label);
        font-weight: 500;
        color: #64748b;
        font-size: 0.75rem;
        text-transform: none;
        text-align: left;
        margin-right: 0.5rem;
      }
      td > div.flex.items-center { 
        justify-content: flex-end; 
        width: 100%;
      }
      td div.font-semibold { text-align: right !important; font-size: 0.8rem !important; color: #1e293b !important; }
      td div.text-sm { text-align: right !important; font-size: 0.65rem !important; }
      td .w-12.h-12 { display: none !important; }
      td.py-3.px-4 .inline-flex { width: auto; }
      td[data-label="Action"] { justify-content: stretch !important; }
      td[data-label="Action"] > div { width: 100% !important; }
      td[data-label="Action"] button { 
        width: 100% !important; 
        border-radius: 8px !important; 
        padding: 0.4rem !important; 
        font-size: 0.75rem !important; 
        font-weight: 600 !important; 
      }
      
      /* Net Salary inside row */
      td[data-label="Net Salary"] .text-2xl {
        font-size: 0.9rem !important;
        font-weight: 700 !important;
      }
      
      /* Status badge inside row */
      td[data-label="Status"] .inline-flex {
        padding: 0.1rem 0.4rem !important;
        font-size: 0.65rem !important;
        border-radius: 4px !important;
      }
      
      /* Generated On inside row */
      td[data-label="Generated On"] .text-gray-600 {
        font-size: 0.7rem !important;
      }
      td[data-label="Generated On"] .text-sm.text-gray-500 {
        font-size: 0.65rem !important;
      }

      /* Inner table container wrapper */
      .overflow-hidden > .p-6 {
        padding: 0.5rem !important;
      }

      /* Summary Footer */
      #payroll-summary-footer {
        padding: 0.4rem !important;
      }
      #payroll-summary-footer > div {
        flex-direction: row !important;
        justify-content: space-between !important;
        width: 100% !important;
        gap: 0.35rem !important;
      }
      #payroll-summary-footer > div span {
        padding: 0.25rem 0.5rem !important;
        font-size: 0.7rem !important;
        border-radius: 6px !important;
        text-align: center !important;
        width: auto !important;
        box-shadow: none !important;
      }
      #payroll-summary-footer > div span.text-base {
        font-size: 0.7rem !important;
      }
    }
  `;

  return (
    <div id="generate-salary-outer-wrapper" className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-100 py-4 px-4">
      <style>{mobileStyles}</style>
      <div className="max-w-full mx-auto">
        {/* Enhanced Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          {/* Left Side - Title and Description */}
          <div className="flex-1 mb-8 lg:mb-0 hidden md:block">
            <div className="flex items-center mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg mr-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div>
                <h1 className="text-4xl lg:text-3xl p-2 font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Payroll Generator
                </h1>
                <p className="text-gray-600 text-lg mt-2">

                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Action Button */}
          <div className="flex-shrink-0 flex justify-center md:block">
            <button
              id="generate-salary-btn"
              onClick={() => {
                if (showForm) resetForm();
                else setShowForm(true);
              }}
              className="group relative px-4 py-2 sm:px-5 sm:py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-md hover:shadow-lg transition-all duration-300 flex justify-center items-center font-semibold overflow-hidden w-full md:w-auto text-sm"
            >
              {/* Animated Background */}
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              {/* Button Content */}
              <div className="relative flex items-center justify-center">
                <svg className="w-4 h-4 mr-2 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {showForm ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  )}
                </svg>
                <span>{showForm ? 'Cancel Generation' : 'Generate Salary'}</span>
              </div>
            </button>

            {/* Quick Stats */}
            {/* {!showForm && (
              <div className="mt-4 flex items-center justify-center text-sm text-gray-600 bg-white/50 backdrop-blur-sm rounded-lg px-4 py-2 border border-gray-200/50">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Ready to generate payroll</span>
              </div>
            )} */}
          </div>
        </div>

        {/* Alerts Section */}
        {error && (
          <div className="mb-8 bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-r-2xl p-6 shadow-sm transform hover:scale-[1.02] transition-transform duration-200">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mt-0.5">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="ml-4 text-red-700 font-medium">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-r-2xl p-6 shadow-sm transform hover:scale-[1.02] transition-transform duration-200">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-0.5">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="ml-4 text-green-700 font-medium">{success}</p>
            </div>
          </div>
        )}

        {/* Payroll Generation Form */}
        {showForm && (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/60 p-6 mb-6 transform transition-all duration-300 hover:shadow-2xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Generate New Payroll
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto mt-4"></div>
            </div>

            <form onSubmit={handleGeneratePayroll} className="space-y-8">
              {/* Basic Information Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200/50">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  Employee Information
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Department *</label>
                    <ResponsiveSelect
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      required={true}
                      placeholder="Select Department"
                      options={departments.map(d => ({ value: d._id, label: d.departmentName || d.name }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Employee *</label>
                    <ResponsiveSelect
                      name="employee"
                      value={formData.employee}
                      onChange={handleInputChange}
                      required={true}
                      placeholder="Choose employee"
                      disabled={!formData.department || filteredEmployees.length === 0}
                      disabledText={!formData.department ? 'Select department first' : 'No employees found'}
                      options={filteredEmployees.map(e => ({ value: e._id, label: `${e.teamMemberName || e.name} - ${e.employeeId}` }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Payroll Month *
                    </label>
                    <input
                      type="month"
                      name="payrollMonth"
                      value={formData.payrollMonth}
                      onChange={handleInputChange}
                      required
                      max={new Date(new Date().setMonth(new Date().getMonth() - 1))
                        .toISOString()
                        .slice(0, 7)}
                      className="w-full px-4 py-3 bg-white/80 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    />
                  </div>

                </div>
              </div>

              {/* Salary & Company Card */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-2xl p-6 border border-purple-200/50">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  Salary & Company Details
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 flex items-center">
                      Base Salary (₹) *
                      {salaryLoading && (
                        <div className="ml-2 flex items-center text-blue-600">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                          <span className="text-xs">Loading...</span>
                        </div>
                      )}
                    </label>
                    <input
                      type="number"
                      name="salary"
                      value={formData.salary}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      required
                      disabled={salaryLoading}
                      className={`w-full px-4 py-3 bg-white/80 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${salaryLoading ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Company</label>
                    <input
                      type="text"
                      value={selectedCompany?.businessName || selectedCompany?.companyName || 'Loading...'}
                      className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-xl text-gray-600"
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* Attendance Card */}
              <div className="bg-gradient-to-br from-cyan-50 to-blue-100 rounded-2xl p-6 border border-cyan-200/50">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    Attendance Summary
                  </h3>
                  {formData.employee && formData.payrollMonth && (
                    <button
                      type="button"
                      onClick={async () => {
                        const summary = await calculateAttendanceSummary(formData.employee, formData.payrollMonth);
                        setFormData(prev => ({
                          ...prev,
                          totalWorkingDays: summary.totalWorkingDays,
                          totalHalfDays: summary.totalHalfDays,
                          totalLeaves: summary.totalLeaves,
                          totalWeeklyHolidays: summary.totalWeeklyHolidays,
                          totalCompanyHolidays: summary.totalCompanyHolidays

                        }));
                      }}
                      className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Recalculate
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: 'Present', value: formData.totalWorkingDays, color: 'green', icon: '✅', description: 'Days with attendance marked' },
                    { label: 'Half Day', value: formData.totalHalfDays, color: 'orange', icon: '🕒', description: 'Partial working days' },
                    { label: 'Holidays', value: (formData.totalWeeklyHolidays || 0) + (formData.totalCompanyHolidays || 0), color: 'purple', icon: '📅', description: 'Weekly & Company holidays' },
                    { label: 'Absent', value: formData.totalLeaves, color: 'red', icon: '❌', description: 'Absent days (excluding holidays)' },
                  ].map((item, index) => {
                    // Handle different color schemes for the new cards
                    let bgColorClass, textColorClass, borderColorClass;
                    switch (item.color) {
                      case 'green':
                        bgColorClass = 'bg-green-500';
                        textColorClass = 'text-green-700';
                        borderColorClass = 'border-green-200';
                        break;
                      case 'orange':
                        bgColorClass = 'bg-orange-500';
                        textColorClass = 'text-orange-700';
                        borderColorClass = 'border-orange-200';
                        break;
                      case 'red':
                        bgColorClass = 'bg-red-500';
                        textColorClass = 'text-red-700';
                        borderColorClass = 'border-red-200';
                        break;
                      case 'purple':
                        bgColorClass = 'bg-purple-500';
                        textColorClass = 'text-purple-700';
                        borderColorClass = 'border-purple-200';
                        break;
                      default:
                        bgColorClass = 'bg-gray-500';
                        textColorClass = 'text-gray-700';
                        borderColorClass = 'border-gray-200';
                    }

                    return (
                      <div key={index} className={`text-center p-4 bg-white/80 rounded-xl border ${borderColorClass} hover:shadow-md transition-all duration-200 hover:-translate-y-1`}>
                        <div className={`w-12 h-12 ${bgColorClass} rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold text-lg`}>
                          {item.icon}
                        </div>
                        <div className="text-2xl font-bold text-gray-800 mb-1">{item.value || 0}</div>
                        <div className={`text-xs font-semibold ${textColorClass} mb-1`}>{item.label}</div>
                        <div className="text-xs text-gray-500">{item.description}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Financial Adjustments */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Deductions */}
                <div className="bg-gradient-to-br from-red-50 to-orange-100 rounded-2xl p-6 border border-red-200/50">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </div>
                      Deductions
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowDeductionForm(true)}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-200 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add
                    </button>
                  </div>

                  {showDeductionForm && (
                    <div className="mb-6 p-4 bg-white/80 rounded-xl border border-red-200">
                      <h4 className="font-semibold text-red-800 mb-4 text-lg">Add New Deduction</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Deduction Name</label>
                          <input
                            type="text"
                            value={newDeduction.name}
                            onChange={(e) => setNewDeduction(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Tax, PF, Insurance"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Amount (₹)</label>
                          <input
                            type="number"
                            value={newDeduction.amount}
                            onChange={(e) => {
                              const value = e.target.value;
                              setNewDeduction(prev => ({ ...prev, amount: value }));
                              const numeric = parseFloat(value);
                              if (!isNaN(numeric) && numeric > 0) {
                                const previewDeductions = [...dynamicDeductions, { amount: numeric }];
                                recalculateTotals(previewDeductions, dynamicIncomes);
                              } else {
                                recalculateTotals(dynamicDeductions, dynamicIncomes);
                              }
                            }}
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          />
                        </div>
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={addDeduction}
                            className="flex-1 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 font-semibold"
                          >
                            Add Deduction
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowDeductionForm(false);
                              setNewDeduction({ name: '', amount: 0 });
                              recalculateTotals();
                            }}
                            className="flex-1 px-4 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200 font-semibold"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {dynamicDeductions.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                        <p className="text-sm">No deductions added yet</p>
                        <p className="text-xs">Click "Add" to create deductions</p>
                      </div>
                    ) : (
                      dynamicDeductions.map((deduction) => (
                        <div key={deduction.id} className="flex items-center justify-between p-4 bg-white/80 rounded-xl border border-red-200 hover:shadow-md transition-shadow duration-200">
                          <div className="flex-1">
                            <div className="font-semibold text-red-800 text-lg">{deduction.name}</div>
                            <div className="text-sm text-red-600">One-time deduction</div>
                          </div>
                          <div className="flex items-center">
                            <div className="text-xl font-bold text-red-600 mr-4">
                              ₹{parseFloat(deduction.amount).toLocaleString('en-IN')}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeDeduction(deduction.id)}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg transition-colors duration-200"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Total Deductions */}
                  <div className="mt-6 pt-4 border-t border-red-200">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-red-800">Total Deductions</span>
                      <span className="text-2xl font-bold text-red-600">
                        ₹{formData.totalDeductions.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Additional Income */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6 border border-green-200/50">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </div>
                      Additional Income
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowIncomeForm(true)}
                      className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors duration-200 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add
                    </button>
                  </div>

                  {showIncomeForm && (
                    <div className="mb-6 p-4 bg-white/80 rounded-xl border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-4 text-lg">Add New Income</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Income Name</label>
                          <input
                            type="text"
                            value={newIncome.name}
                            onChange={(e) => setNewIncome(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Bonus, Incentive, Allowance"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Amount (₹)</label>
                          <input
                            type="number"
                            value={newIncome.amount}
                            onChange={(e) => {
                              const value = e.target.value;
                              setNewIncome(prev => ({ ...prev, amount: value }));
                              const numeric = parseFloat(value);
                              if (!isNaN(numeric) && numeric > 0) {
                                const previewIncomes = [...dynamicIncomes, { amount: numeric }];
                                recalculateTotals(dynamicDeductions, previewIncomes);
                              } else {
                                recalculateTotals(dynamicDeductions, dynamicIncomes);
                              }
                            }}
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                        </div>
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={addIncome}
                            className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200 font-semibold"
                          >
                            Add Income
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowIncomeForm(false);
                              setNewIncome({ name: '', amount: 0 });
                              recalculateTotals();
                            }}
                            className="flex-1 px-4 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200 font-semibold"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {dynamicIncomes.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <p className="text-sm">No additional income added yet</p>
                        <p className="text-xs">Click "Add" to create income entries</p>
                      </div>
                    ) : (
                      dynamicIncomes.map((income) => (
                        <div key={income.id} className="flex items-center justify-between p-4 bg-white/80 rounded-xl border border-green-200 hover:shadow-md transition-shadow duration-200">
                          <div className="flex-1">
                            <div className="font-semibold text-green-800 text-lg">{income.name}</div>
                            <div className="text-sm text-green-600">Additional income</div>
                          </div>
                          <div className="flex items-center">
                            <div className="text-xl font-bold text-green-600 mr-4">
                              ₹{parseFloat(income.amount).toLocaleString('en-IN')}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeIncome(income.id)}
                              className="p-2 text-green-500 hover:text-green-700 hover:bg-green-100 rounded-lg transition-colors duration-200"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Total Incomes */}
                  <div className="mt-6 pt-4 border-t border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-green-800">Total Additional Income</span>
                      <span className="text-2xl font-bold text-green-600">
                        ₹{formData.totalIncomes.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Final Calculation */}
              <div className="bg-gradient-to-br from-yellow-50 to-amber-100 rounded-2xl p-8 border border-yellow-200/50">
                <h3 className="text-2xl font-bold text-gray-800 text-center mb-8 flex items-center justify-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  Final Salary Calculation
                </h3>

                {/* Calculation Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-6 bg-white/80 rounded-2xl border border-blue-200 hover:shadow-lg transition-shadow duration-200">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      ₹{(() => {
                        const calc = calculateProportionalSalary(
                          formData.salary,
                          formData.totalWorkingDays,
                          formData.totalHalfDays,
                          formData.payrollMonth,
                          formData.totalWeeklyHolidays,
                          formData.totalCompanyHolidays
                        );
                        return calc.grossSalary.toLocaleString('en-IN');
                      })()}
                    </div>
                    <div className="text-blue-600 font-semibold">Gross Salary</div>
                    <div className="text-xs text-gray-500 mt-1">Including holiday pay</div>
                  </div>

                  <div className="text-center p-6 bg-white/80 rounded-2xl border border-green-200 hover:shadow-lg transition-shadow duration-200">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      +₹{formData.totalIncomes.toLocaleString('en-IN')}
                    </div>
                    <div className="text-green-600 font-semibold">Additional Income</div>
                    <div className="text-xs text-gray-500 mt-1">Bonuses & allowances</div>
                  </div>

                  <div className="text-center p-6 bg-white/80 rounded-2xl border border-red-200 hover:shadow-lg transition-shadow duration-200">
                    <div className="text-3xl font-bold text-red-600 mb-2">
                      -₹{(formData.totalDeductions + formData.pfAmount + formData.esicAmount).toLocaleString('en-IN')}
                    </div>
                    <div className="text-red-600 font-semibold">Total Deductions</div>
                    <div className="text-xs text-gray-500 mt-1">Including PF, ESI & others</div>
                  </div>
                </div>


                {/* PF and ESI Breakdown */}
                {(employeePfEsi.pfPercentage || employeePfEsi.esicPercentage) && formData.salary && (
                  <div className="bg-gradient-to-br from-orange-50 to-red-100 rounded-2xl p-6 border border-orange-200/50 mb-8">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      PF & ESI Deductions
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* PF Details */}
                      {employeePfEsi.pfPercentage && (
                        <div className="bg-white/80 rounded-xl p-4 border border-orange-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-medium text-orange-700">Provident Fund (PF)</div>
                            <div className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full font-medium">
                              {employeePfEsi.pfPercentage}%
                            </div>
                          </div>
                          <div className="text-2xl font-bold text-orange-600">₹{formData.pfAmount.toLocaleString('en-IN')}</div>

                        </div>
                      )}

                      {/* ESI Details */}
                      {employeePfEsi.esicPercentage && (
                        <div className="bg-white/80 rounded-xl p-4 border border-red-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-medium text-red-700">Employee State Insurance (ESI)</div>
                            <div className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-medium">
                              {employeePfEsi.esicPercentage}%
                            </div>
                          </div>
                          <div className="text-2xl font-bold text-red-600">₹{formData.esicAmount.toLocaleString('en-IN')}</div>

                        </div>
                      )}
                    </div>

                    {/* PF/ESI Status */}
                    <div className="mt-4 p-4 bg-orange-100 rounded-lg border border-orange-200">
                      <div className="flex items-center text-orange-800">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span className="text-sm font-medium">
                          {employeePfEsi.loading ? 'Loading PF/ESI data...' :
                            employeePfEsi.error ? `Error: ${employeePfEsi.error}` :
                              'PF and ESI deductions are calculated based on employee\'s configured percentages and gross salary.'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Net Salary Display */}
                <div className="text-center bg-white/80 rounded-2xl p-8 border-2 border-yellow-300">
                  <div className="text-lg font-semibold text-gray-600 mb-4">NET SALARY PAYABLE</div>
                  <div className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                    ₹{Number(formData.netSalary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-sm text-gray-500">
                    Final amount to be disbursed to the employee
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center pt-6">
                <button
                  type="submit"
                  disabled={isGenerating || !formData.employee || !formData.payrollMonth}
                  className="px-12 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none font-semibold text-lg w-full lg:w-auto"
                >
                  {isGenerating ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                      Generating Payroll...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Generate Payroll
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Payroll History */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg md:shadow-2xl border border-slate-100 md:border-white/60 overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-gray-200/60 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
              <h2 className="text-lg md:text-xl font-bold text-slate-800 mr-2">
                Payroll History
              </h2>
              <div id="payroll-history-badges" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <div className="flex items-center bg-emerald-50/60 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md border border-emerald-100 text-emerald-700">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5"></div>
                  <span className="text-[10px] sm:text-xs font-medium">Auto Calculations</span>
                </div>
                <div className="flex items-center bg-blue-50/60 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md border border-blue-100 text-blue-700">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1.5"></div>
                  <span className="text-[10px] sm:text-xs font-medium">Real-time Updates</span>
                </div>
                <div className="flex items-center bg-purple-50/60 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md border border-purple-100 text-purple-700">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-1.5"></div>
                  <span className="text-[10px] sm:text-xs font-medium">Secure Processing</span>
                </div>
              </div>
            </div>
            <button
              id="refresh-history-btn"
              onClick={fetchPayrollHistory}
              disabled={historyLoading}
              className="w-full md:w-auto px-4 py-2 sm:px-5 sm:py-2.5 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 text-indigo-700 rounded-xl shadow-sm transition-all duration-300 disabled:opacity-50 flex items-center justify-center font-semibold text-sm"
            >
              {historyLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-700 mr-2"></div>
                  Loading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh History
                </>
              )}
            </button>
          </div>

          <div className="p-6">
            {historyLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading payroll history...</p>
              </div>
            ) : payrollHistory.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <p className="text-gray-600 text-lg mb-2">No payroll records found</p>
                <p className="text-gray-500">Generate your first payroll to see it here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200/60">
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Employee</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Month</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Net Salary</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Status</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Generated On</th>
                      <th className="text-center py-3 px-4 text-gray-600 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrollHistory.map((payroll, index) => (
                      <tr key={payroll._id} className="border-b border-gray-200/60 hover:bg-gray-50/50 transition-colors duration-200">
                        <td className="py-3 px-4" data-label="Employee">
                          <div className="flex items-center">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold mr-4">
                              {(payroll.employee?.teamMemberName || 'N/A').charAt(0)}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-800 text-lg">
                                {payroll.employee?.teamMemberName || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {payroll.employee?.department?.departmentName || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4" data-label="Month">
                          <div className="text-gray-700 font-medium">
                            {payroll.payrollMonth ? new Date(payroll.payrollMonth + '-01').toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long'
                            }) : 'N/A'}
                          </div>
                        </td>
                        <td className="py-3 px-4" data-label="Net Salary">
                          <div className="text-2xl font-bold text-green-600">
                            ₹{Number(payroll.netSalary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </td>
                        <td className="py-3 px-4" data-label="Status">
                          <span className={`inline-flex px-4 py-2 rounded-full text-sm font-semibold ${payroll.status === 'Paid'
                            ? 'bg-green-100 text-green-800'
                            : payroll.status === 'Pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-blue-100 text-blue-800'
                            }`}>
                            {payroll.status || 'Generated'}
                          </span>
                        </td>
                        <td className="py-3 px-4" data-label="Generated On">
                          <div className="text-gray-600">
                            {payroll.createdAt ? new Date(payroll.createdAt).toLocaleDateString('en-IN') : 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {payroll.createdAt ? new Date(payroll.createdAt).toLocaleTimeString('en-IN', {
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : ''}
                          </div>
                        </td>

                        <td className="py-3 px-4" data-label="Action">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => generatePDF(payroll._id)}
                              className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 text-sm"
                            >
                              Download PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Summary Footer */}
                <div id="payroll-summary-footer" className="bg-slate-50 border-t border-slate-200 px-4 py-3">
                  <div className="flex flex-row items-center justify-between text-xs sm:text-sm text-slate-700 gap-3">
                    <span className="font-semibold px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg border border-slate-200/80">
                      Total Records: {payrollHistory.length}
                    </span>
                    <span className="font-semibold px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-100">
                      Total Net Salary: ₹{Number(payrollHistory.reduce((sum, payroll) => sum + (payroll.netSalary || 0), 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

};

export default GenerateSalary;

